import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Building2,
  ChevronUp,
  ChevronDown,
  Check,
  X,
  GripVertical,
  HelpCircle,
  Search,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import { LoadingSpinner } from '../../ui/Loading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/Table';
import Input from '../../ui/Input';
import { GameProvider } from '../../../types';
import { useGameProviders, useReorderGameProviders, useUpdateGameProvider } from '../../../hooks/useApi';
import { useAuth } from '../../../hooks/useAuth';
import { cn } from '../../../utils';
import GameProviderStatusBadge from './GameProviderStatusBadge';
import {
  formatDepositLimit,
  formatProviderCurrency,
  getMaxDeposit,
  getMinDeposit,
  getProviderCurrency,
} from './gameProviderDisplayHelpers';

interface GameProviderListProps {
  onEdit: (provider: GameProvider) => void;
  onDelete: (provider: GameProvider) => void;
  onCreate: () => void;
}

const TAG_PRESETS = ['HOT', 'NEW', 'TOP', 'POPULAR'] as const;

function getDisplayOrder(p: GameProvider): number {
  const v = p.displayOrder ?? p.display_order;
  if (v === undefined || v === null) return 1000;
  const n = typeof v === 'number' ? v : parseInt(String(v), 10);
  return Number.isFinite(n) ? n : 1000;
}

function getClientTag(p: GameProvider): string {
  const t = p.clientTag ?? p.client_tag;
  return t != null && String(t).trim() ? String(t).trim() : '';
}

function reorderIdsByMove(ids: string[], fromIndex: number, toIndex: number): string[] {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= ids.length || toIndex >= ids.length) {
    return ids;
  }
  const copy = [...ids];
  const [removed] = copy.splice(fromIndex, 1);
  copy.splice(toIndex, 0, removed);
  return copy;
}

function KioskOrderHeader() {
  return (
    <TableHead className="whitespace-nowrap px-3 py-2.5 h-auto text-xs font-medium uppercase tracking-wide text-muted-foreground">
      <span className="inline-flex items-center gap-1.5 normal-case">
        Kiosk order
        <span
          className="inline-flex cursor-help text-muted-foreground/90"
          title="Lower numbers appear first on Home, Deposit, and Withdrawal. Default 1000 = end of list. Use Arrange order to drag rows or arrow buttons, then Save kiosk order."
        >
          <HelpCircle className="h-3.5 w-3.5" aria-hidden />
        </span>
      </span>
    </TableHead>
  );
}

interface InlineClientTagCellProps {
  provider: GameProvider;
  disabled?: boolean;
}

const InlineClientTagCell: React.FC<InlineClientTagCellProps> = ({ provider, disabled }) => {
  const updateMutation = useUpdateGameProvider();
  const current = getClientTag(provider);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(current);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setValue(current);
  }, [current, editing]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const save = async () => {
    const trimmed = value.trim();
    const nextTag = trimmed === '' ? null : trimmed.slice(0, 64);
    const prevNormalized = current === '' ? null : current;
    if (nextTag === prevNormalized) {
      setEditing(false);
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: provider.id,
        data: { clientTag: nextTag },
      });
      setEditing(false);
    } catch {
      /* toast in hook */
    }
  };

  const cancel = () => {
    setValue(current);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="min-w-[200px] max-w-[260px] space-y-1.5" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          className="w-full px-2 py-1.5 text-sm border rounded-md bg-background"
          value={value}
          maxLength={64}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void save();
            if (e.key === 'Escape') cancel();
          }}
          onBlur={() => void save()}
          disabled={updateMutation.isLoading}
          placeholder="Badge text on kiosk card"
          aria-label="Kiosk card tag"
        />
        <div className="flex flex-wrap gap-1">
          {TAG_PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setValue(p)}
            >
              {p}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => !disabled && setEditing(true)}
      className={cn(
        'max-w-[200px] truncate text-left text-sm transition-colors',
        disabled && 'cursor-not-allowed text-muted-foreground',
        !disabled &&
          current &&
          'rounded-md bg-primary/[0.07] px-2.5 py-1 font-medium text-foreground ring-1 ring-primary/15 hover:bg-primary/[0.11]',
        !disabled &&
          !current &&
          'rounded-md border border-dashed border-primary/35 bg-primary/[0.02] px-2.5 py-1 italic text-primary/90 hover:border-primary/50 hover:bg-primary/[0.06]'
      )}
      title={current || 'Add a badge on the kiosk game card'}
    >
      {current || 'Add tag'}
    </button>
  );
};

const GameProviderList: React.FC<GameProviderListProps> = ({ onEdit, onDelete, onCreate }) => {
  const { user: currentAdmin } = useAuth();
  const { data: providersData, isLoading, error, refetch } = useGameProviders();
  const reorderMutation = useReorderGameProviders();

  const canManageProviders = currentAdmin?.role === 'system_admin' || currentAdmin?.role === 'super_admin';

  const providers = useMemo(() => providersData?.data?.items || [], [providersData?.data?.items]);
  const [reorderMode, setReorderMode] = useState(false);
  const [orderedIds, setOrderedIds] = useState<string[]>([]);
  const [orderBaseline, setOrderBaseline] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const draggedIdRef = useRef<string | null>(null);

  const searchLower = search.trim().toLowerCase();
  const filteredProviders = useMemo(() => {
    if (!searchLower) return providers;
    return providers.filter(
      (p) =>
        p.name.toLowerCase().includes(searchLower) ||
        p.code.toLowerCase().includes(searchLower)
    );
  }, [providers, searchLower]);

  const startReorder = useCallback(() => {
    const ids = providers.map((p) => p.id);
    setOrderedIds(ids);
    setOrderBaseline(ids);
    setReorderMode(true);
    setSearch('');
  }, [providers]);

  const cancelReorder = useCallback(() => {
    setReorderMode(false);
    setOrderedIds([]);
    setOrderBaseline([]);
    setDragOverId(null);
    draggedIdRef.current = null;
  }, []);

  const providerById = useMemo(() => {
    const m = new Map<string, GameProvider>();
    providers.forEach((p) => m.set(p.id, p));
    return m;
  }, [providers]);

  const rowsForTable = useMemo(() => {
    if (!reorderMode) return filteredProviders;
    return orderedIds.map((id) => providerById.get(id)).filter((p): p is GameProvider => !!p);
  }, [reorderMode, filteredProviders, orderedIds, providerById]);

  const orderDirty = useMemo(() => {
    if (!reorderMode || orderBaseline.length === 0) return false;
    if (orderedIds.length !== orderBaseline.length) return true;
    return orderedIds.some((id, i) => id !== orderBaseline[i]);
  }, [reorderMode, orderedIds, orderBaseline]);

  const moveUp = (index: number) => {
    if (index <= 0) return;
    setOrderedIds((prev) => reorderIdsByMove(prev, index, index - 1));
  };

  const moveDown = (index: number) => {
    setOrderedIds((prev) => {
      if (index >= prev.length - 1) return prev;
      return reorderIdsByMove(prev, index, index + 1);
    });
  };

  const onRowDragStart = (e: React.DragEvent, id: string) => {
    draggedIdRef.current = id;
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onRowDragOver = (e: React.DragEvent, _id: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onRowDragEnter = (id: string) => {
    setDragOverId(id);
  };

  const onRowDragLeave = () => {
    setDragOverId(null);
  };

  const onRowDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    setDragOverId(null);
    const draggedId = e.dataTransfer.getData('text/plain') || draggedIdRef.current;
    draggedIdRef.current = null;
    if (!draggedId || draggedId === targetId) return;
    setOrderedIds((prev) => {
      const fromIndex = prev.indexOf(draggedId);
      const toIndex = prev.indexOf(targetId);
      if (fromIndex < 0 || toIndex < 0) return prev;
      return reorderIdsByMove(prev, fromIndex, toIndex);
    });
  };

  const saveOrder = async () => {
    if (orderedIds.length === 0 || !orderDirty) return;
    try {
      await reorderMutation.mutateAsync(orderedIds);
      setReorderMode(false);
      setOrderedIds([]);
      setOrderBaseline([]);
      refetch();
    } catch {
      /* toast in hook */
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive">Failed to load game providers</p>
          <button
            onClick={() => refetch()}
            className="mt-2 text-sm text-primary hover:underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const thClass =
    'px-3 py-2.5 h-auto text-xs font-medium uppercase tracking-wide text-muted-foreground';
  const tdClass = 'px-3 py-2.5';

  return (
    <Card className="overflow-hidden border-border/80 shadow-sm">
      <CardHeader className="border-b bg-muted/30 pb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="flex items-center text-lg font-semibold tracking-tight">
            <Building2 className="mr-2 h-5 w-5 shrink-0 text-muted-foreground" />
            Game providers
            <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-sm font-normal tabular-nums text-muted-foreground">
              {providers.length}
            </span>
          </CardTitle>
          <div className="flex w-full flex-wrap justify-end gap-2 sm:w-auto">
            {reorderMode ? (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={saveOrder}
                  disabled={reorderMutation.isLoading || !orderDirty}
                >
                  <Check className="mr-1 h-4 w-4" />
                  Save kiosk order
                </Button>
                <Button variant="outline" size="sm" onClick={cancelReorder} disabled={reorderMutation.isLoading} className="border-border/80">
                  <X className="mr-1 h-4 w-4" />
                  Cancel
                </Button>
              </>
            ) : (
              <Button variant="outline" size="sm" onClick={startReorder} className="border-border/80">
                <GripVertical className="mr-1 h-4 w-4" />
                Arrange kiosk order
              </Button>
            )}
            {canManageProviders && (
              <Button onClick={onCreate} className="flex items-center gap-2" size="sm">
                <Plus className="h-4 w-4" />
                Create provider
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => refetch()} className="border-border/80">
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-6">
        {reorderMode && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-4 py-3 text-sm text-amber-950 dark:border-amber-500/25 dark:bg-amber-950/40 dark:text-amber-50">
            <strong className="font-semibold">Arrange mode:</strong> drag the{' '}
            <span className="rounded bg-amber-100 px-1 font-mono text-xs dark:bg-amber-900/80">⋮⋮</span> handle, or use arrows.
            Order applies to <strong>Home</strong>, <strong>Deposit</strong>, and <strong>Withdrawal</strong> on the kiosk.
            {orderDirty && (
              <span className="ml-2 font-medium text-amber-800 dark:text-amber-200">· Unsaved changes</span>
            )}
          </div>
        )}

        {!reorderMode && (
          <div className="relative max-w-lg">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="h-9 pl-9"
              placeholder="Search by name or code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search game providers"
            />
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner />
          </div>
        ) : providers.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No game providers found.</p>
        ) : filteredProviders.length === 0 && !reorderMode ? (
          <p className="text-center text-muted-foreground py-8">No matching providers. Try a different keyword.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border/60 bg-card shadow-sm">
            <Table className="min-w-[960px]">
              <TableHeader className="border-b border-border/60 bg-muted/40 [&_tr]:border-border/60">
                <TableRow className="border-0 hover:bg-transparent">
                  {reorderMode && (
                    <TableHead className={cn(thClass, 'w-12 text-center normal-case')}>
                      <span title="Drag to reorder">
                        <GripVertical className="inline h-4 w-4 text-muted-foreground" />
                      </span>
                    </TableHead>
                  )}
                  {reorderMode && (
                    <TableHead className={cn(thClass, 'w-[88px] normal-case')}>Nudge</TableHead>
                  )}
                  <KioskOrderHeader />
                  <TableHead className={thClass}>Kiosk tag</TableHead>
                  <TableHead className={thClass}>Code</TableHead>
                  <TableHead className={thClass}>Name</TableHead>
                  <TableHead className={thClass}>Status</TableHead>
                  <TableHead className={thClass}>Currency</TableHead>
                  <TableHead className={cn(thClass, 'text-right')}>Min dep.</TableHead>
                  <TableHead className={cn(thClass, 'text-right')}>Max dep.</TableHead>
                  <TableHead className={cn(thClass, 'text-right')}>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rowsForTable.map((provider, index) => (
                  <TableRow
                    key={provider.id}
                    className={cn(
                      'border-border/40 hover:bg-muted/25',
                      reorderMode && dragOverId === provider.id && 'bg-primary/[0.06] ring-2 ring-inset ring-primary/30'
                    )}
                    onDragOver={(e) => onRowDragOver(e, provider.id)}
                    onDragEnter={() => reorderMode && onRowDragEnter(provider.id)}
                    onDragLeave={onRowDragLeave}
                    onDrop={(e) => reorderMode && onRowDrop(e, provider.id)}
                  >
                    {reorderMode && (
                      <TableCell className={cn(tdClass, 'cursor-grab text-center align-middle active:cursor-grabbing')}>
                        <div
                          draggable
                          onDragStart={(e) => onRowDragStart(e, provider.id)}
                          className="inline-flex rounded-md p-1 hover:bg-muted"
                          title="Drag to move"
                          role="button"
                          aria-label="Drag to reorder row"
                        >
                          <GripVertical className="h-5 w-5 text-muted-foreground" />
                        </div>
                      </TableCell>
                    )}
                    {reorderMode && (
                      <TableCell className={tdClass}>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 border-border/80 p-0"
                            disabled={index === 0}
                            onClick={() => moveUp(index)}
                            aria-label="Move up"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 border-border/80 p-0"
                            disabled={index === rowsForTable.length - 1}
                            onClick={() => moveDown(index)}
                            aria-label="Move down"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                    <TableCell className={cn(tdClass, 'font-mono text-sm tabular-nums text-muted-foreground')}>
                      {getDisplayOrder(provider)}
                    </TableCell>
                    <TableCell className={cn(tdClass, 'align-top')}>
                      <InlineClientTagCell provider={provider} disabled={reorderMode} />
                    </TableCell>
                    <TableCell className={tdClass}>
                      <code className="rounded-md bg-muted/70 px-2 py-1 font-mono text-xs text-foreground ring-1 ring-border/50">
                        {provider.code}
                      </code>
                    </TableCell>
                    <TableCell className={cn(tdClass, 'font-semibold text-foreground')}>{provider.name}</TableCell>
                    <TableCell className={tdClass}>
                      <GameProviderStatusBadge status={provider.status} />
                    </TableCell>
                    <TableCell className={cn(tdClass, 'font-mono text-sm text-muted-foreground')}>
                      {formatProviderCurrency(provider)}
                    </TableCell>
                    <TableCell className={cn(tdClass, 'text-right tabular-nums text-sm text-muted-foreground')}>
                      {formatDepositLimit(getMinDeposit(provider), getProviderCurrency(provider))}
                    </TableCell>
                    <TableCell className={cn(tdClass, 'text-right tabular-nums text-sm text-muted-foreground')}>
                      {formatDepositLimit(getMaxDeposit(provider), getProviderCurrency(provider))}
                    </TableCell>
                    <TableCell className={cn(tdClass, 'text-right')}>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                          onClick={() => onEdit(provider)}
                          disabled={reorderMode}
                          aria-label="Edit provider"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {canManageProviders && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 border-0 p-0 text-destructive hover:bg-destructive/10"
                            onClick={() => onDelete(provider)}
                            disabled={reorderMode}
                            aria-label="Delete provider"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GameProviderList;
